import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Volume2, ArrowRight, Sparkles, ThumbsDown, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { playAppAudio } from "@/lib/appAudio";
import { ttsOK } from "@/lib/languages";
import { useI18n } from "@/context/I18nContext";

const TZ = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ""; } })();
const KIND_BG = { recall: "bg-lavender", retrieve: "bg-lavender", use_it: "bg-peach", situation: "bg-sky" };

// Phase 7.1 — Life Curriculum, surfaced only inside Today. Deep planner underneath, 3–5 calm cards above.
export function LifeCurriculum({ onFinish, onUnavailable, extras }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  const opened = useRef(new Set());

  useEffect(() => {
    api.get("/curriculum/today", { params: { tz: TZ } })
      .then((r) => (r.data.plan ? setData(r.data) : onUnavailable()))
      .catch(() => onUnavailable());
  }, []); // eslint-disable-line

  // AI wording arrives in the background; merge it only into cards the learner hasn't opened yet.
  const promptsPending = data?.plan?.prompts_state === "pending";
  useEffect(() => {
    if (!promptsPending) return undefined;
    let n = 0; let timer;
    const poll = () => {
      timer = setTimeout(async () => {
        n += 1;
        try {
          const r = await api.get("/curriculum/today", { params: { tz: TZ } });
          const fresh = r.data.plan;
          setData((d) => ({ ...d, plan: { ...d.plan, prompts_state: fresh.prompts_state, items: d.plan.items.map((x) => {
            const f = fresh.items.find((y) => y.item_id === x.item_id);
            return f && !x.prompt && f.prompt && x.status === "pending" && !opened.current.has(x.item_id) ? { ...x, prompt: f.prompt } : x;
          }) } }));
          if (fresh.prompts_state === "pending" && n < 6) poll();
        } catch { /* templates stay */ }
      }, 2500 + n * 1500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [promptsPending]);

  const items = data?.plan?.items || [];
  const current = items.find((i) => i.status === "pending");
  const updateItem = useCallback((it) => setData((d) => ({ ...d, plan: { ...d.plan, items: d.plan.items.map((x) => (x.item_id === it.item_id ? it : x)) } })), []);

  useEffect(() => { if (data && !current && !finished) { setFinished(true); onFinish?.(); } }, [data, current, finished, onFinish]);

  const keepGoing = async () => {
    setBusy(true);
    try { const r = await api.post("/curriculum/today/more", null, { params: { tz: TZ } }); setData((d) => ({ ...d, plan: r.data.plan })); setFinished(false); }
    catch { /* stay on summary */ } finally { setBusy(false); }
  };

  if (!data) return <div className="nb-card p-8 mt-6 text-center text-sm text-gray-600" data-testid="lc-loading"><Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />{t("lc.loading")}</div>;
  const doneCount = items.filter((i) => i.status !== "pending").length;

  return (
    <section className="mt-6" data-testid="life-curriculum">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{t("lc.forYou")}</div>
          <h2 className="font-cabinet font-extrabold text-2xl" data-testid="lc-title">{t("lc.title")}</h2>
          <p className="text-sm text-gray-600" data-testid="lc-subtitle">{data.plan.has_history ? t("lc.subtitle") : t("lc.cold")}</p>
        </div>
        {current && <div className="text-xs font-bold text-gray-500 shrink-0" data-testid="lc-progress">{t("lc.progress", { n: doneCount + 1, total: items.length })}</div>}
      </div>
      <div className="flex gap-1.5 mt-3" aria-hidden>{items.map((i) => <span key={i.item_id} className={`h-1.5 flex-1 rounded-full border border-ink ${i.status === "pending" ? "bg-white" : "bg-ink"}`} />)}</div>
      <AnimatePresence mode="wait">
        {current ? (
          <motion.div key={current.item_id} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.22 }}>
            <LcCard item={current} lang={data.target_lang} onUpdate={updateItem} onOpen={(id) => opened.current.add(id)} />
          </motion.div>
        ) : (
          <motion.div key="done" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <LcSummary items={items} canContinue={!data.plan.continued} busy={busy} onMore={keepGoing} extras={extras} srsDue={data.srs_due_total} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function LcCard({ item, lang, onUpdate, onOpen }) {
  const { t } = useI18n();
  const [shown, setShown] = useState(item.status !== "pending" ? item : null);
  const [frozen] = useState(item.prompt); // wording never changes while the card is open
  useEffect(() => { onOpen?.(item.item_id); api.post(`/curriculum/items/${item.item_id}/open`).catch(() => {}); }, [item.item_id]); // eslint-disable-line
  const post = async (path, body) => { const r = await api.post(`/curriculum/items/${item.item_id}/${path}`, body); return r.data.item; };
  const advance = (it) => onUpdate(it);
  const skip = async () => { try { advance(await post("skip")); } catch { /* ignore */ } };
  const notUseful = async () => { try { advance(await post("not-useful")); } catch { /* ignore */ } };
  return (
    <div className={`nb-card ${KIND_BG[item.kind]} p-6 mt-4`} data-testid={`lc-card-${item.kind}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="nb-tag bg-white" data-testid="lc-kind">{t(`lc.kind.${item.kind}`)}</span>
        <span className="text-xs text-gray-700 italic" data-testid="lc-reason">{t(`lc.reason.${item.reason}`)}</span>
      </div>
      {item.kind === "recall" || item.kind === "retrieve"
        ? <RecallBody item={item} lang={lang} onDone={(correct) => post("recall", { correct }).then(advance).catch(() => {})} />
        : <WriteBody item={{ ...item, prompt: frozen }} lang={lang} shown={shown} onAnswer={async (answer) => { const it = await post("answer", { answer }); setShown(it); return it; }} onNext={() => advance(shown)} />}
      {!shown && (
        <div className="flex justify-between mt-4 text-xs text-gray-600">
          <button onClick={skip} className="hover:text-ink" data-testid="lc-skip">{t("lc.skip")}</button>
          {item.kind !== "situation" && <button onClick={notUseful} className="hover:text-ink inline-flex items-center gap-1" data-testid="lc-not-useful"><ThumbsDown className="w-3.5 h-3.5" /> {t("lc.notUseful")}</button>}
        </div>
      )}
    </div>
  );
}

function RecallBody({ item, lang, onDone }) {
  const { t } = useI18n();
  const [revealed, setRevealed] = useState(false);
  const [noVoice, setNoVoice] = useState(false);
  const front = item.kind === "recall" ? item.display : item.gloss;
  return (
    <div className="text-center mt-5">
      {item.kind === "retrieve" && <div className="text-sm text-gray-700">{t("lc.howSay")}</div>}
      <div className="font-cabinet font-extrabold text-3xl mt-1" dir="auto" lang={item.kind === "recall" ? lang : undefined} data-testid="lc-front">{front}</div>
      {revealed ? (
        <>
          <div className="mt-3 flex items-center justify-center gap-2">
            <span className="text-xl font-bold" dir="auto" data-testid="lc-back">{item.kind === "recall" ? item.gloss : item.display}</span>
            {ttsOK(lang) && !noVoice && <button onClick={() => playAppAudio(`/curriculum/items/${item.item_id}/audio`).catch(() => setNoVoice(true))} className="w-8 h-8 bg-white border-2 border-ink rounded-full flex items-center justify-center" data-testid="lc-listen"><Volume2 className="w-4 h-4" /></button>}
          </div>
          {item.example && <div className="text-sm text-gray-600 italic mt-1" dir="auto" lang={lang}>"{item.example}"</div>}
          <div className="flex gap-3 mt-5">
            <button onClick={() => onDone(false)} className="nb-btn-outline flex-1 py-3" data-testid="lc-missed"><X className="w-5 h-5" /> {t("today.missedIt")}</button>
            <button onClick={() => onDone(true)} className="nb-btn-mint flex-1 py-3" data-testid="lc-got"><Check className="w-5 h-5" /> {t("today.gotIt")}</button>
          </div>
        </>
      ) : <button onClick={() => setRevealed(true)} className="nb-btn-outline mt-5 px-6 py-2" data-testid="lc-reveal">{t("today.reveal")}</button>}
    </div>
  );
}

function WriteBody({ item, lang, shown, onAnswer, onNext }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const topic = item.topic ? t(`lc.topic.${item.topic}`) : "";
  const instruction = item.prompt?.instruction || (item.kind === "use_it"
    ? (topic ? t("lc.useItTopicTpl", { word: item.display, topic }) : t("lc.useItTpl", { word: item.display }))
    : t("lc.situationTpl", { function: t(`lc.fn.${item.function}`), topic: topic || t("lc.topic.everyday_life") }));
  const submit = async () => { if (!text.trim()) return; setSending(true); try { await onAnswer(text.trim()); } catch { /* keep draft */ } finally { setSending(false); } };
  const res = shown?.result;
  return (
    <div className="mt-4">
      {item.kind === "situation" && <div className="text-xs font-bold text-gray-600">{t(`lc.fn.${item.function}`)}</div>}
      {item.prompt?.situation && <p className="text-sm text-gray-800 mt-1" dir="auto" data-testid="lc-situation">{item.prompt.situation}</p>}
      <p className="font-cabinet font-bold text-lg mt-2" dir="auto" data-testid="lc-instruction">{instruction}</p>
      {!res ? (
        <>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={400} dir="auto" lang={lang}
            placeholder={t("lc.write")} className="w-full mt-3 p-3 border-2 border-ink rounded-lg bg-white text-base" data-testid="lc-answer-input" />
          <button onClick={submit} disabled={sending || !text.trim()} className="nb-btn-primary w-full mt-3 py-3 disabled:opacity-60" data-testid="lc-check">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("lc.check")} <ArrowRight className="w-5 h-5" /></>}
          </button>
        </>
      ) : (
        <div className="mt-3 space-y-2" data-testid="lc-feedback">
          <div className="bg-white/70 border-2 border-ink rounded-lg p-3 text-sm" dir="auto" lang={lang}>{res.answer}</div>
          {res.feedback ? (
            <>
              <div className="bg-white border-2 border-ink rounded-lg p-3">
                <div className="text-xs font-bold uppercase text-gray-500">{t("lc.natural")}</div>
                <div className="mt-0.5" dir="auto" lang={lang} data-testid="lc-natural">{res.feedback.already_natural ? t("lc.alreadyNatural") : res.feedback.natural}</div>
              </div>
              {res.feedback.tip && <div className="text-sm flex gap-2" dir="auto" data-testid="lc-tip"><Sparkles className="w-4 h-4 shrink-0 mt-0.5 text-coral" /><span><b>{t("lc.tip")}:</b> {res.feedback.tip}</span></div>}
            </>
          ) : item.prompt?.sample && (
            <div className="bg-white border-2 border-ink rounded-lg p-3" data-testid="lc-sample">
              <div className="text-xs font-bold uppercase text-gray-500">{t("lc.example")}</div>
              <div className="mt-0.5" dir="auto" lang={lang}>{item.prompt.sample}</div>
            </div>
          )}
          <button onClick={onNext} className="nb-btn-primary w-full mt-2 py-3" data-testid="lc-next">{t("lc.next")} <ArrowRight className="w-5 h-5" /></button>
        </div>
      )}
    </div>
  );
}

function LcSummary({ items, canContinue, busy, onMore, extras, srsDue }) {
  const { t } = useI18n();
  const done = items.filter((i) => i.status === "done");
  const recalled = done.filter((i) => ["recall", "retrieve"].includes(i.kind) && i.result?.correct).length;
  const used = done.filter((i) => i.kind === "use_it").length;
  const situations = done.filter((i) => i.kind === "situation").length;
  const parts = [recalled && t("lc.recalled", { count: recalled }), used && t("lc.used", { count: used }), situations && t("lc.situations", { count: situations })].filter(Boolean);
  return (
    <div className="nb-card bg-mint p-6 mt-4 text-center" data-testid="lc-summary">
      <Check className="w-10 h-10 mx-auto stroke-[3]" />
      <h3 className="font-cabinet font-extrabold text-2xl mt-2" data-testid="lc-done-title">{t("lc.doneTitle")}</h3>
      {parts.length > 0 && <p className="text-sm mt-1" data-testid="lc-summary-counts">{parts.join(" · ")}</p>}
      {canContinue && <button onClick={onMore} disabled={busy} className="nb-btn-outline mt-5 px-6 py-2 disabled:opacity-60" data-testid="lc-keep-going">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("lc.keepGoing")}</button>}
      {extras && <div className="mt-5 pt-4 border-t-2 border-ink/20 text-sm">{extras(srsDue)}</div>}
    </div>
  );
}
